import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { placeFlexPackIntoCart } from '../../../utilities/api'

import {
  formatCurrency,
  formatNumber,
  getSinglarOrPluralWord,
} from '../../../utilities/helpers'
import {
  flexPackCustomFeeLines,
  flexPackDisplayPrice,
  flexPackPriceIncludesTaxesAndFees,
} from '../../../utilities/flexPackPrice'
import {
  resolveBrandLogo,
  resolvePrimaryColor,
} from '../../PurchaseFlow/utils/branding'
import { Spinner } from '../../LoadingContainer/Spinner'

function customFeeLabel(line) {
  if (line.option === 'percent') {
    const pct = Number(line.amount)
    const shown = Number.isInteger(pct) ? String(pct) : String(pct)
    return `Includes ${shown}% ${line.name}`
  }
  return `Includes ${formatCurrency(line.amount)} ${line.name}`
}

const FLEX_PACK_STEPS = [
  {
    title: 'Buy the vouchers',
    description: 'Pick how many you want. They land in your wallet right away.',
  },
  {
    title: 'Redeem when you know',
    description:
      'Choose a game any time before kickoff and pick your seat then.',
  },
  {
    title: 'Share what you skip',
    description: 'Transfer a voucher to a friend if you cannot make a game.',
  },
]

export default function FlexPackDetails({ flexPack }) {
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState(false)

  const handleGoBack = () => {
    navigate(`/${flexPack?.organization?.slug || flexPack?.venue?.slug}`)
  }

  const checkout = () => {
    setIsLoading(true)
    placeFlexPackIntoCart(flexPack.id)
      .then((res) => {
        sessionStorage.setItem('cart', JSON.stringify({ cartId: res.data.id }))
        navigate('/checkout')
      })
      .catch((err) => {
        console.error(err)
      })
      .finally(() => setIsLoading(false))
  }

  if (!flexPack) return null

  const organization = flexPack.organization
  const voucherCount = Number(flexPack.gameTickets || 0)
  const voucherLabel = getSinglarOrPluralWord(voucherCount, 'voucher')
  const primaryColor =
    flexPack.primaryColor ||
    flexPack.season?.primaryColor ||
    resolvePrimaryColor(null, organization)
  const logoSrc =
    resolveBrandLogo(null, organization) || flexPack.image?.url || null
  const providerLabel = organization?.name
    ? `Official ticketing marketplace for ${organization.name} venues`
    : 'Official ticketing marketplace'
  const seasonLabel = flexPack.season?.name
    ? `Flex pack · ${flexPack.season.name}`
    : 'Flex pack'
  const fees = flexPackCustomFeeLines(flexPack)
  const priceIncludesTaxesAndFees = flexPackPriceIncludesTaxesAndFees(flexPack)

  return (
    <div
      id="view-flex-pack"
      className="flex-pack-purchase"
      style={{ '--fp-primary': primaryColor }}
    >
      <header className="flex-pack-hero">
        <div className="flex-pack-shell flex-pack-hero__inner">
          <div className="flex-pack-hero__top">
            <button
              type="button"
              className="flex-pack-back"
              onClick={handleGoBack}
              aria-label="Back to organization"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <p className="flex-pack-eyebrow">{seasonLabel}</p>
          </div>

          <div className="flex-pack-hero__content">
            {logoSrc ? (
              <div className="flex-pack-logo-tile">
                <img src={logoSrc} alt={organization?.name || flexPack.name} />
              </div>
            ) : null}

            <div className="flex-pack-hero__copy">
              <h1>{flexPack.name}</h1>
              <div className="flex-pack-tags">
                {flexPack.venue?.name ? (
                  <span>{flexPack.venue.name}</span>
                ) : null}
                <span>Any home game</span>
                <span>Redeem any time</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-pack-shell flex-pack-main">
        <div className="flex-pack-information">
          <section className="flex-pack-how">
            <p className="flex-pack-section-label">How the flex pack works</p>
            <div className="flex-pack-steps">
              {FLEX_PACK_STEPS.map((step, index) => (
                <article className="flex-pack-step" key={step.title}>
                  <span className="flex-pack-step__number">{index + 1}</span>
                  <h2>{step.title}</h2>
                  <p>{step.description}</p>
                </article>
              ))}
            </div>
          </section>

          {flexPack.description ? (
            <section className="flex-pack-more">
              <p className="flex-pack-section-label">More info</p>
              <p>{flexPack.description}</p>
            </section>
          ) : null}
        </div>

        <aside className="flex-pack-checkout-card">
          <p className="flex-pack-checkout-card__label">
            Flex pack · {formatNumber(voucherCount)} {voucherLabel}
          </p>
          <p className="flex-pack-checkout-card__price">
            {formatCurrency(flexPackDisplayPrice(flexPack))}
          </p>
          <p className="flex-pack-checkout-card__fees">
            {priceIncludesTaxesAndFees
              ? 'Taxes and fees included'
              : 'Plus taxes and fees'}
          </p>
          {fees.map((line) => (
            <p className="flex-pack-checkout-card__fees" key={line.id}>
              {customFeeLabel(line)}
            </p>
          ))}
          <button
            type="button"
            id="checkout-btn"
            disabled={isLoading || flexPack.isSoldOut}
            onClick={checkout}
          >
            {isLoading ? (
              <Spinner />
            ) : flexPack.isSoldOut ? (
              'Sold out'
            ) : (
              `Get ${formatNumber(voucherCount)} ${voucherLabel}`
            )}
          </button>
          <p className="flex-pack-checkout-card__note">
            One voucher, one ticket to any home game. Pick your seat when you
            redeem.
          </p>
        </aside>
      </main>

      <div className="flex-pack-market-footer" role="contentinfo">
        <div className="flex-pack-market-footer__inner">
          <span className="flex-pack-market-footer__brand">blocktickets</span>
          <span className="flex-pack-market-footer__divider" aria-hidden />
          <span className="flex-pack-market-footer__label">
            {providerLabel}
          </span>
        </div>
      </div>
    </div>
  )
}
